/******************************************************************************
 *** BgtMbsdsWholDpstList ** 운영비 수입/지출 전체 조회 * 수입결의서 리스트
*******************************************************************************/
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import Modal from "@/js/common/ui/modal/Modal";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import Util from "@/js/common/Util";
import React from "react";
import DialogTitleBar from "../../common/DialogTitleBar";
import BgtMbsdsDpstViewDialog from "./dialog/BgtMbsdsDpstViewDialog";

interface Props {
    ognzCd          : string;  // 조직코드(지부)
    acntgYear       : string;  // 회계연도
    searchAtrzSttsCd: string;  // 진행상태
    searchKywd      : string;  // 검색어(적요)
    sttDt           : string;  // 검색 시작일(수입일자 기준)
    endDt           : string;  // 검색 종료일(수입일자 기준)
    setEndDt        : Function;// set 검색 종료일(초기화 프롭스)
    acntgMonth      : string;  // 월별 조회
}
const BgtMbsdsWholDpstList = ( {ognzCd, acntgYear, searchAtrzSttsCd, searchKywd, sttDt, endDt, acntgMonth, setEndDt}: Props ) => {
    const [incomeDataArr, setIncomeDataArr] = React.useState<any[]>([]);  // 수입결의서 목록
    const [sumIncomeAmt , setSumIncomeAmt ] = React.useState<string>(""); // 조합비 수입내역 합계

    const [mbsdsDpstModalOpen , setMbsdsDpstModalOpen] = React.useState<boolean>(false); // 수입결의서 상세정보 모달
    const [mbsdsDpstDocId     , setMbsdsDpstDocId    ] = React.useState<string>(""); // 수입결의서 문서번호

    // GridTable 컬럼 정의
    const incomeFldInfs: fldInfsProps[] = [
        { type: "date" , fldNm: "dpstDt"        , width: 120, headAlign: "center", dataAlign: "center", label: "수입일자" },
        { type: "text" , fldNm: "bgtSbjct1stNm" , width: 120, headAlign: "center", dataAlign: "center", label: "관"       },
        { type: "text" , fldNm: "bgtSbjct2stNm" , width: 120, headAlign: "center", dataAlign: "center", label: "항"       },
        { type: "text" , fldNm: "bgtSbjct3stNm" , width: 120, headAlign: "center", dataAlign: "center", label: "목"       },
        { type: "text" , fldNm: "abstrCn"       , width: 190, headAlign: "center", dataAlign: "left"  , label: "적요"     },
        { type: "comma", fldNm: "dpstAmt"       , width: 120, headAlign: "center", dataAlign: "right" , label: "금액"     },
        { type: "text" , fldNm: "atrzSttsNm"    , width:  80, headAlign: "center", dataAlign: "center", label: "진행상태" },
        { type: "text" , fldNm: "crtDt"         , width: 120, headAlign: "center", dataAlign: "center", label: "작성일시" },
        { type: "text" , fldNm: "dpstRsdocNo"   , width: 140, headAlign: "center", dataAlign: "center", label: "문서번호" },
    ];

    // 운영비 수입결의서 전체 목록 조회
    React.useEffect(() => {
        // 수입일자 검색 조건 확인
        if (Util.isNotEmpty(sttDt) && Util.isNotEmpty(endDt)) {
            if (sttDt > endDt) {
                AppContext.openAlert({ 
                    title: "수입일자 검색 조건 확인", 
                    msg: "수입일자의 검색 시작일자가 종료일자보다 느립니다.", 
                    icon: "warning" 
                }); 
                setEndDt("")
                return;
            } 
        }
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/mbsds/whol/select-whol-dpst-list.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd           : ognzCd          , // 조직코드
                acntgYear       : acntgYear       , // 회계연도
                searchAtrzSttsCd: searchAtrzSttsCd, // 진행상태
                sttDt           : sttDt           , // 검색 시작일(수입일자 기준)
                endDt           : endDt           , // 검색 종료일(수입일자 기준)
                acntgMonth      : acntgMonth        // 월별 검색
            },
            onSuccessFn: (res: any) => {
                setIncomeDataArr(res.result.list);
                setSumIncomeAmt(res.result.listSum.sumDpstAmt); // 수입결의서 목록 합계

                AppContext.hideSpinner();
            },
        });
    }, [ognzCd, acntgYear, searchAtrzSttsCd, sttDt, endDt, acntgMonth, setEndDt]);

    React.useEffect(() => {
        let sum = 0;
        for (const row of Util.filterFieldData(incomeDataArr, incomeFldInfs, "abstrCn", searchKywd)) {
            sum += Number(row.dpstAmt) || 0;
        }
        setSumIncomeAmt(Util.comma(String(sum)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incomeDataArr, searchKywd]);

    return (
        <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
            <div className="dpFlx aiC jcSb mb10 h44">
                <span className="fm-b fs16 fc007bff ls1 fw700">수입 결의서 목록</span>
                <div className="mt20 dpFlx">
                    <span className="fm-Pretendard-Light fs16 ls1 fc222222 pr3">합계</span>
                    <span className="fm-Pretendard-Medium fs16 ls1 fc007bff ml10">{Util.comma(sumIncomeAmt)}</span>
                    <span className="fm-Pretendard-Light fs13 ls1 fc222222 pl2 pr3 mt3">원</span>
                </div>
            </div>

            {/* 그리드 */}
            <GridTable
                tableInfo={{
                    tableHeadVsb: true,
                    styleType: AdminGridStyle,
                    fldInfs: incomeFldInfs,
                    ordFld: { name: "dpstDt", direction: "desc" },
                    dataArr: Util.filterFieldData(incomeDataArr, incomeFldInfs, "abstrCn", searchKywd),
                    maxRowNums: [30, 50, 100],
                    theadTrHeight: 40,
                    tbodyTrHeight: 35,
                    paginateClass: "pt10",
                    paginateStyle: {
                        buttonOnColor: AppContext.boardThemeColor,
                        buttonWidth: 22, buttonHeight: 24,
                        buttonMarginLeft: 1, buttonMarginRight: 1,
                        fontSize: 13, color: "#666666", letterSpacing: -0.8, fontFamily: "Pretendard-Regular",
                        borderRadius: 3,
                    },
                    footerVsb: true,
                    rowClickFunc: (item: any) => {  // row click
                        setMbsdsDpstDocId(item.mbsdsDpstDocId);
                        setMbsdsDpstModalOpen(true);
                    },
                }}
            />

            {/* 수입결의서 상세정보 모달 */}
            <Modal modalOpen={mbsdsDpstModalOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title="수입결의서 상세정보" />}
                onCloseFunc={() => {
                    setMbsdsDpstDocId("");
                    setMbsdsDpstModalOpen(false);
                }}
            >
                <BgtMbsdsDpstViewDialog
                    mbsdsDpstDocId        = {mbsdsDpstDocId}   // 수입결의서 문서 ID
                    setMbsdsDpstModalOpen = {setMbsdsDpstModalOpen}
                />
            </Modal>

        </div>
    )
}

export default BgtMbsdsWholDpstList;